import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge, Gap, Spacing, Text, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useShowPricesByGroup from 'hooks/useShowPricesByGroup';
import useMixDiscountFaresByAvailability from 'hooks/useMixDiscountFaresByAvailability';
import usePurchasePricingSeatZones from 'hooks/usePurchasePricingSeatZones';
import usePricingTotal from 'hooks/usePricingTotal';
import {
  getDiscountAvailability,
  getPassengerTotalBySelectionIndex,
} from 'hooks/usePricingBeforeCheckout';
import usePurchase from 'hooks/store/usePurchase';
import useSeatsBus from 'hooks/store/useSeatsBus';
import OverbookingBadge from 'ui/atoms/OverbookingBadge';
import { safeParsePrice } from 'utils/currency';
import { percentBelow } from 'utils/Helpers';
import { hasOversoldSeats } from 'utils/overbooking';
import { legFromFragments, roundTripFareApplies } from 'utils/roundTripFare';
import { hasLapChildData } from 'utils/lapChild';
import adjacentLogo from 'images/seats/adjacentLogo.svg';
import { DiscountBadge as UrgencyDiscountBadge } from 'components/search/DynamicPricingDiscount';
import PricingRow from '../../ui/atoms/PricingRow';
import discountGFA from '../../images/brands-unique/gfa/early-discount.png';
import discountETN from '../../images/brands-unique/etn/discountETN.png';
import { resolveSeatZoneForDetails } from '../../utils/seatPricing';
import {
  isBfoPricing,
  isBfoSupportedJourney,
  resolveBfoEffectiveSeatPrice,
} from '../../utils/purchase/baseFareOverride';
import ProductsBreakdown from './ProductsBreakdown';

const earlyDiscountIcons = {
  etn: discountETN,
  costaline: discountETN,
  gho: discountETN,
  gfa: discountGFA,
  default: 'emojiSparkles',
};

/**
 * Gets the custom early discount icon for brands that use one in results.
 * @param {string} brand - Current brand.
 * @returns {string|undefined} Brand-specific early discount icon.
 */
const getEarlyDiscountIcon = (brand) => earlyDiscountIcons[brand];

// These are the possible descriptions returned by the Cruz del Sur WS, its the only tenant using it
const fareDescriptionMap = {
  INSUPERABLE: 'Insuperable',
  OCASION: 'Ocasión',
  VIP: 'VIP',
  LOCURA: 'Locura',
  REGULAR: 'Regular',
};

/**
 * Get fare description
 * @param {string} description - Fare description
 * @param {boolean} showSeatFareDescription - Whether to show fare description
 * @returns {string|null} - Formatted fare description or null if invalid/hidden
 */
const getFareDescription = (description, showSeatFareDescription) => {
  const isValidDescription = description && fareDescriptionMap[description];
  if (!showSeatFareDescription || !isValidDescription) {
    return null;
  }
  return fareDescriptionMap[description];
};

/**
 * Process ticket data for seat-based pricing
 * @param {object} ticket - The ticket item
 * @param {function} t - Translation function
 * @returns {object} - Processed seat-based ticket data
 */
const processSeatBasedTicket = (ticket, t) => {
  const passengerName = '';
  const hasEarlyDiscount = false;
  const showSeat = !!ticket.number;
  const showSeatFloor = !!ticket.seatFloor;
  const seatHasNumber = Number.isFinite(Number(ticket.number));
  const discountLabels = { label: t('label.discount') };

  return {
    ...ticket,
    passengerName,
    hasEarlyDiscount,
    showSeat,
    showSeatFloor,
    seatHasNumber,
    discountLabels,
    amount: ticket.price,
    discountAmount: 0,
    passengerId: ticket.index,
    subtotal: 0,
    taxes: 0,
    total: ticket.price,
    seat: ticket.number,
    isAdjacentSeat: false,
    category: 'seat',
  };
};

/**
 * Rebuild the ticket's fare, discount and percentage on the carrier's own basis.
 *
 * `officePrice` is what the carrier sells the seat for at the window, snapshotted onto the ticket
 * when it was locked. `amount` is the platform's quoted fare, which for this carrier is already
 * net of a dynamic discount — 151.000 against a window fare of 190.000. Both describe the same
 * charge; only one matches the table the carrier reconciles against (MID-642).
 *
 * One subtraction covers every case, so nothing here has to ask which kind of discount applied:
 *
 *   category benefit   190.000 - 116.000 = 74.000   (the carrier's own `descuentoaplicado`)
 *   operational only   190.000 - 151.000 = 39.000   (invisible on this screen until now)
 *   neither            190.000 - 190.000 =      0   (no discount row)
 *
 * The first line is also what the e-ticket prints, which derives its fare row the other way round
 * as `total + category_discount_value`. The two agree by algebra, not coincidence.
 *
 * @param {object} ticket - The ticket item
 * @returns {{fare: number, discount: number, percent: number}|null} Carrier basis, or null when
 *   the ticket carries no window fare and the platform basis applies.
 */
const carrierFareBasis = (ticket) => {
  const fare = Number(ticket.officePrice) || 0;
  const charged = Number(ticket.total) || 0;
  // A window fare below the charge is not a discount of a negative amount, it is bad data — bail
  // to the platform basis rather than render it.
  //
  // Taxes bail for the same reason, one step earlier. The discount here is measured against
  // `total` (gross) while the subtotal row below renders `subtotal` (net of taxes), so the block
  // only closes where the two are the same number — `fare − discount == subtotal` holds at
  // `taxes: 0` and nowhere else. Rápido Ochoa quotes tax-free fares, which is why the rows add
  // up today; the helper is generic and the flag is per-brand, so a tax-bearing brand enabling it
  // would render `1200 − 270 / Subtotal 801.7 / Impuestos 128.3`, a subtraction landing on no
  // adjacent row. Which row the discount should be measured against there is a question the spec
  // does not answer yet, so the brand stays on the platform basis until it does.
  if (fare <= 0 || charged <= 0 || fare < charged || Number(ticket.taxes) > 0) return null;

  // Floors, never rounds. 39.000 off a window fare of 190.000 is 20,52%, and rounding advertises
  // it as 21% — a discount larger than the one granted. The carrier asked for this explicitly
  // (ticket 74549471, 28 Sep): the figure may be whole, but it must land below the real
  // percentage and never above it.
  //
  // Through `percentBelow`, the same expression the results badge floors, rather than a second
  // copy of the arithmetic. A copy is what let the two disagree: this side subtracted first and
  // `formatPercent` divided first, so 152000 against 190000 read 20% here and 19% there.
  //
  // Not a ternary on `discount > 0`: the guard above already gives `fare >= charged`, so the
  // zero case is `Math.floor(0)`, which is the same 0.
  const discount = fare - charged;
  return { fare, discount, percent: Math.floor(percentBelow(fare, charged)) };
};

/**
 * Process ticket data for regular passenger pricing
 * @param {object} ticket - The ticket item
 * @param {function} t - Translation function
 * @param {boolean} purchasePricingDetailedDiscount - Whether to show detailed discount
 * @param {?{fare: number, discount: number, percent: number}} carrier - This ticket's carrier
 *   basis, already decided for the whole leg, or null to render on the platform basis
 * @returns {object} - Processed passenger ticket data
 */
const processPassengerTicket = (ticket, t, purchasePricingDetailedDiscount, carrier) => {
  const passengerName = `${ticket.firstName} ${ticket.lastName || ''}`;
  const charged = Number(ticket.total) || 0;
  // The early-discount branch renders the platform's own programme copy and icon, which names a
  // benefit the carrier did not grant. On a carrier ticket the row stays on the generic discount
  // label instead — the same word the e-ticket prints.
  const hasEarlyDiscount = !carrier && ticket.discountReason === 'compra anticipada';
  const showSeat = !!ticket.seat;
  const showSeatFloor = !!ticket.seatFloor;
  const seatHasNumber = Number.isFinite(Number(ticket.seat));

  if (carrier) {
    return {
      ...ticket,
      passengerName,
      hasEarlyDiscount,
      showSeat,
      showSeatFloor,
      seatHasNumber,
      // The passenger's row shows what that passenger costs, whichever discount landed: 116000
      // with the category benefit, 151000 with the operational one, 190000 with neither. The
      // window fare it was discounted from is the section row above, summed across the leg.
      rowPrice: charged,
      // Deliberately alongside `amount`, never over it: `seatPrice` is derived from `amount` and
      // rendered through the seat-pricing path, which this change does not touch.
      carrierFare: carrier.fare,
      carrierDiscount: carrier.discount,
      // Generic "Descuento", not `categoryDiscountName`. The carrier's own names for its
      // benefits — "Niños", "Voy y Vuelvo Digital" — read as passenger types beside a row that
      // already states the category, and its e-ticket prints the generic word too
      // (`discount_header`). Matching the document beats naming the programme.
      discountLabels: {
        label: t('label.discount'),
        // Omitted below 1%, where the floor leaves 0 while the discount row still carries a real
        // amount: a `-0%` badge states a discount it cannot show. The results badge already
        // suppresses that case through `hasReadablePercent`; this is the same rule on this side.
        discountLabel: carrier.percent > 0 ? `-${carrier.percent}%` : undefined,
      },
    };
  }

  let discountLabels = { label: t('label.discount') };
  if (purchasePricingDetailedDiscount && ticket.discountReason && ticket.discountPercent) {
    discountLabels = {
      label: t('label.savings', {
        reason: ticket.discountReason,
      }),
      discountLabel: `-${ticket.discountPercent}%`,
    };
  } else if (ticket.discountPercent) {
    // Fallback for when feature flag is disabled but we have discount percentage
    discountLabels = {
      ...discountLabels,
      discountLabel: `-${ticket.discountPercent}%`,
    };
  }

  // `amount + taxes`, not `amount` alone, and `total` when the payload carries no fare.
  //
  // The row has to be the figure the discount comes off, or the panel reads `116000 - 35000 =
  // 116000` (MID-642). But `amount` is the fare NET of taxes, so on a tax-bearing brand it is
  // neither what the passenger pays nor the section row's summand: an undiscounted Costaline seat
  // went from 640 to 551,70, which this change claimed left undiscounted tickets alone. Adding
  // the taxes back restores that — and keeps the subtraction closing, since the rows below are
  // `discount`, `subtotal` and `taxes`, so `row - discount == subtotal + taxes == total` on every
  // brand. Rápido Ochoa quotes tax-free fares, so its 151.000 does not move.
  //
  // The fallback is not defensive. `Api::V2::TicketSerializer#amount` served a blank on a
  // category discount with no coupon until platform-purchase#2910, and this path is not behind
  // `USE_CARRIER_FARE_BASIS` — every brand reaches it. Without the fallback a funnel deployed
  // ahead of that backend renders the passenger row at $0.
  // `parseFloat`, not `Number`: `Number(null)` and `Number('')` are a finite 0, so a blank fare
  // would pass the guard and render the taxes alone as the passenger's price.
  const fare = Number.parseFloat(ticket.amount);
  const rowPrice = Number.isFinite(fare)
    ? fare + (Number(ticket.taxes) || 0)
    : Number(ticket.total) || 0;

  return {
    ...ticket,
    passengerName,
    hasEarlyDiscount,
    showSeat,
    showSeatFloor,
    seatHasNumber,
    discountLabels,
    rowPrice,
  };
};

/**
 * Process ticket data for display based on ticket type
 * @param {object} ticket - The ticket item
 * @param {boolean} useSeatBased - Whether to use seat-based pricing
 * @param {function} t - Translation function
 * @param {boolean} purchasePricingDetailedDiscount - Whether to show detailed discount
 * @param {?{fare: number, discount: number, percent: number}} carrier - This ticket's carrier
 *   basis, already decided for the whole leg, or null to render on the platform basis
 * @returns {object} - Processed ticket data
 */
const processTicketData = (ticket, useSeatBased, t, purchasePricingDetailedDiscount, carrier) => {
  return useSeatBased
    ? processSeatBasedTicket(ticket, t)
    : processPassengerTicket(ticket, t, purchasePricingDetailedDiscount, carrier);
};

/**
 * Renders the legacy detailed discount badge.
 * @param {object} props - Component props.
 * @param {string} props.label - Discount percentage label.
 * @returns {JSX.Element|null} Legacy discount badge.
 */
const LegacyDiscountBadge = ({ label }) => {
  if (!label) return null;

  return (
    <Badge roundCorners type="success" smallPadding>
      <Text color="white" elementType="span" size="XS" weight="semibold">
        {label}
      </Text>
    </Badge>
  );
};

LegacyDiscountBadge.propTypes = {
  label: PropTypes.string,
};

/**
 * Renders the passenger discount visual indicator.
 * @param {object} props - Component props.
 * @param {number|string} props.discountAmount - Discount amount.
 * @param {boolean} props.hasEarlyDiscount - Whether the ticket has an early/urgency discount.
 * @param {object} props.discountLabels - Discount labels.
 * @param {string} props.brand - Current brand.
 * @param {boolean} props.showDetailedDiscount - Whether the legacy detailed discount badge is enabled.
 * @param {boolean} props.showDiscountUrgency - Whether the urgency discount UI is enabled.
 * @returns {JSX.Element|null} Discount visual indicator.
 */
const PassengerDiscountIndicator = ({
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  brand,
  showDetailedDiscount,
  showDiscountUrgency,
}) => {
  if (Number(discountAmount) <= 0) return null;

  const discountLabel = discountLabels?.discountLabel;

  if (showDiscountUrgency) {
    const discountIcon = hasEarlyDiscount ? getEarlyDiscountIcon(brand) : null;
    if (!discountLabel) return discountIcon ? <Icon type={discountIcon} size="S" /> : null;

    return (
      <>
        {discountIcon && <Icon type={discountIcon} size="S" />}
        <UrgencyDiscountBadge percent={discountLabel} />
      </>
    );
  }

  if (hasEarlyDiscount) {
    return <Icon type={getEarlyDiscountIcon(brand) ?? earlyDiscountIcons.default} size="S" />;
  }

  if (!showDetailedDiscount) return null;

  return <LegacyDiscountBadge label={discountLabel} />;
};

PassengerDiscountIndicator.propTypes = {
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  brand: PropTypes.string,
  showDetailedDiscount: PropTypes.bool,
  showDiscountUrgency: PropTypes.bool,
};

/**
 * Row component
 * @param {object} props - label, discountLabel, price, hasDiscount, isMain, icon, primary, description, titleSize.
 * @param {string} props.label - Label to show.
 * @param {string} props.discountLabel - Discount label to show.
 * @param {number} props.price - Price to show.
 * @param {boolean} props.hasDiscount - If has discount.
 * @param {boolean} props.isMain - If is main.
 * @param {string} props.icon - Icon to show.
 * @param {boolean} props.primary - If is primary.
 * @param {string} props.description - Description to show.
 * @param {string} props.titleSize - Title size.
 * @returns {JSX.Element} - RowComponent JSX element.
 */
const RowComponent = ({
  label,
  discountLabel,
  price,
  hasDiscount,
  isMain,
  icon,
  primary,
  description,
  titleSize,
}) => {
  return (
    <PricingRow
      label={label}
      discountLabel={discountLabel}
      isMain={isMain}
      hasDiscount={hasDiscount}
      price={price}
      primary={primary}
      icon={icon}
      description={description}
      titleSize={titleSize}
    />
  );
};

RowComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleSize: PropTypes.string,
  discountLabel: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  hasDiscount: PropTypes.bool,
  isMain: PropTypes.bool,
  icon: PropTypes.string,
  primary: PropTypes.bool,
  description: PropTypes.string,
};

/**
 * PassengerTicketHeader component.
 * Display the passenger or seat information header.
 * @param {object} props - passengerName, totalPrice, isOpen, hasCollapsibleTrigger, discountAmount, hasEarlyDiscount, discountLabels, brand, showDiscount, showDiscountUrgency.
 * @param {string} props.passengerName - Passenger name.
 * @param {number|string} props.totalPrice - Total price for the passenger.
 * @param {boolean} props.isOpen - Whether the collapsible section is open.
 * @param {boolean} props.hasCollapsibleTrigger - Whether to show collapsed passenger view.
 * @param {number|string} props.discountAmount - Discount amount.
 * @param {boolean} props.hasEarlyDiscount - Whether has early discount.
 * @param {object} props.discountLabels - Discount labels.
 * @param {string} props.brand - Brand name.
 * @param {boolean} props.showDiscount - Display legacy discount badge.
 * @param {boolean} props.showDiscountUrgency - Display urgency discount badge.
 * @returns {JSX.Element} PassengerTicketHeader component.
 */
const PassengerTicketHeader = ({
  passengerName,
  totalPrice,
  isOpen,
  hasCollapsibleTrigger,
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  brand,
  showDiscount,
  showDiscountUrgency,
}) => (
  <Spacing size="S" alignItems="center" justifyContent="space-between">
    <Text
      weight={hasCollapsibleTrigger || isOpen ? 'semibold' : 'regular'}
      size="S"
      color={hasCollapsibleTrigger || isOpen ? 'grayStrong' : 'grayMedium'}
    >
      {passengerName}
    </Text>

    <Spacing size="XS" alignItems="center">
      <PassengerDiscountIndicator
        discountAmount={discountAmount}
        hasEarlyDiscount={hasEarlyDiscount}
        discountLabels={discountLabels}
        brand={brand}
        showDetailedDiscount={showDiscount}
        showDiscountUrgency={showDiscountUrgency}
      />

      <LocalizedCurrency
        currencyStyle="currency"
        color={hasCollapsibleTrigger || isOpen ? 'grayStrong' : 'grayMedium'}
        price={safeParsePrice(totalPrice)}
        decimals={2}
        size="S"
        weight={hasCollapsibleTrigger || isOpen ? 'semibold' : 'regular'}
      />
    </Spacing>
  </Spacing>
);

PassengerTicketHeader.propTypes = {
  passengerName: PropTypes.string,
  totalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOpen: PropTypes.bool,
  hasCollapsibleTrigger: PropTypes.bool,
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  brand: PropTypes.string,
  showDiscount: PropTypes.bool,
  showDiscountUrgency: PropTypes.bool,
};

/**
 * TicketPriceDetails component
 * Displays pricing details for a ticket
 *
 * @param {Object} props - Component props
 * @param {string} props.tripSlug - Trip slug
 * @param {string|number} props.passengerId - Passenger ID
 * @param {string} props.segment - Trip segment.
 * @param {number} props.carrierFare - Provider window fare this ticket's discount was taken from
 * @param {number} props.discountAmount - Discount amount
 * @param {boolean} props.hasEarlyDiscount - Whether has early discount
 * @param {Object} props.discountLabels - Discount labels
 * @param {number} props.subtotal - Subtotal
 * @param {number} props.taxes - Taxes
 * @param {number} props.toll - Per-passenger toll amount
 * @param {number} props.boardingTax - Per-passenger boarding tax amount
 * @param {number} props.managementCosts - Per-passenger management costs amount
 * @param {boolean} props.showBreakdown - Whether to itemize the fare breakdown (toll, boarding tax, management costs)
 * @param {string[]} props.canShowAtZero - Breakdown keys allowed to render when their value is zero
 * @param {boolean} props.customManagementCostsLabel - Whether to use the custom management costs label
 * @param {string} props.brand - Brand name
 * @param {string} props.externalCouponCode - External coupon code
 * @param {boolean} props.showDiscountUrgency - Whether the urgency discount UI is enabled.
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} - TicketPriceDetails component
 */
const TicketPriceDetails = ({
  tripSlug,
  passengerId,
  segment,
  carrierFare,
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  subtotal,
  taxes,
  toll,
  boardingTax,
  managementCosts,
  showBreakdown,
  canShowAtZero,
  customManagementCostsLabel,
  brand,
  externalCouponCode,
  showDiscountUrgency,
  t,
}) => {
  const tollAmount = Number(toll) || 0;
  const boardingTaxAmount = Number(boardingTax) || 0;
  const managementCostsAmount = Number(managementCosts) || 0;

  // When the breakdown is enabled, the subtotal is split into the net fare plus its
  // components (toll, boarding tax, management costs) so the lines add up to the passenger
  // total, mirroring the PurchasePricingTrip layout. Otherwise the fare stays lumped.
  const fareSubtotal = showBreakdown
    ? Number(subtotal) - tollAmount - boardingTaxAmount - managementCostsAmount
    : subtotal;

  const showToll = showBreakdown && (tollAmount > 0 || canShowAtZero.includes('toll'));
  const showBoardingTax =
    showBreakdown && (boardingTaxAmount > 0 || canShowAtZero.includes('boardingTax'));
  const showManagementCosts =
    showBreakdown && (managementCostsAmount > 0 || canShowAtZero.includes('managementCosts'));
  // In breakdown mode the tax line only shows when there are taxes (e.g. empresaunida has none),
  // matching how the other breakdown rows are gated. Outside breakdown mode it always shows.
  const showTaxes = !showBreakdown || Number(taxes) > 0 || canShowAtZero.includes('taxes');
  const earlyDiscountIcon = getEarlyDiscountIcon(brand);
  const legacyEarlyDiscountIcon = earlyDiscountIcon ?? earlyDiscountIcons.default;
  const resolvedEarlyDiscountIcon = showDiscountUrgency
    ? earlyDiscountIcon
    : legacyEarlyDiscountIcon;

  return (
    <>
      {/* On the carrier fare basis the passenger row shows what that passenger costs and the
          section row above sums the window fares across the leg — so with more than one
          passenger no fare sits beside its own discount, and the block reads `116000 - 74000 =
          116000`, the shape of the defect this all started with. This row puts the fare the
          discount was measured from directly above it, which is also what the percent badge is
          a percentage of.

          Gated on the discount too, not just on the fare: with nothing taken off, the fare IS
          the subtotal, and the row would repeat 190000 under a passenger row already showing
          190000 under a section row showing it a third time. */}
      {Number(carrierFare) > 0 && Number(discountAmount) > 0 && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-carrier-fare`}
          label={t('label.carrier_fare')}
          price={carrierFare}
        />
      )}

      {discountAmount > 0 &&
        (hasEarlyDiscount ? (
          <RowComponent
            label={t('trips:early_discount')}
            price={discountAmount}
            hasDiscount
            primary
            icon={resolvedEarlyDiscountIcon}
          />
        ) : (
          <RowComponent label={discountLabels.label} price={discountAmount} hasDiscount />
        ))}

      {fareSubtotal > 0 && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-subtotal`}
          label={t('label.subtotal')}
          price={fareSubtotal}
        />
      )}

      {showToll && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-toll`}
          label={t('toll')}
          price={tollAmount}
        />
      )}

      {showBoardingTax && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-boarding-tax`}
          label={t('boardingTax')}
          price={boardingTaxAmount}
        />
      )}

      {showManagementCosts && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-management-costs`}
          label={t(customManagementCostsLabel ? 'commissioningRate' : 'managementCosts')}
          price={managementCostsAmount}
        />
      )}

      {showTaxes && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-tax`}
          label={t('extra_price')}
          price={taxes}
        />
      )}

      {/*
        Products go last so the ticket arithmetic closes first: the passenger header states the
        fare plus its taxes, which the discount and subtotal rows resolve to `subtotal + taxes`
        and which excludes products. Anything drawn between that header and the rows that compose
        it reads as part of that total.
      */}
      <ProductsBreakdown tripSlug={tripSlug} passengerId={passengerId} segment={segment} />

      {externalCouponCode && (
        <Spacing size="S" alignItems="center">
          <Text color="grayMedium">{t('label.coupon')}:</Text>
          <Badge hasShadow roundCorners type="success" smallPadding>
            <Text color="white" elementType="span" size="S" weight="semibold">
              {externalCouponCode}
            </Text>
          </Badge>
        </Spacing>
      )}
    </>
  );
};

TicketPriceDetails.propTypes = {
  tripSlug: PropTypes.string.isRequired,
  passengerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  segment: PropTypes.string,
  carrierFare: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  subtotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  taxes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  toll: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  boardingTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  managementCosts: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showBreakdown: PropTypes.bool,
  canShowAtZero: PropTypes.arrayOf(PropTypes.string),
  customManagementCostsLabel: PropTypes.bool,
  brand: PropTypes.string,
  externalCouponCode: PropTypes.string,
  showDiscountUrgency: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

/**
 * SeatBasedDetails component.
 * Renders seat number, deck badge, zone dot, and price for seat-based pricing rows.
 *
 * @param {Object} props - ticketData, t, showSeatFloor, seatFloor, seatZone, seatPrice.
 * @param {Object} props.ticketData - Ticket data object.
 * @param {Function} props.t - Translation function.
 * @param {boolean} props.showSeatFloor - Whether to show seat floor badge.
 * @param {string|number} props.seatFloor - Seat floor label.
 * @param {{ color: string|null, zoneKey: string, label: string }} props.seatZone - Resolved zone for display.
 * @param {number} props.seatPrice - Seat price for the row.
 * @returns {JSX.Element} SeatBasedDetails component.
 */
const SeatBasedDetails = ({ ticketData, t, showSeatFloor, seatFloor, seatZone, seatPrice }) => (
  <Spacing size="XS" vertical fullWidth>
    <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
      <Text size="S" color="grayLight" weight="regular">
        {ticketData.showSeat && ticketData.seatHasNumber
          ? `${t('general:seat')} ${ticketData.seat}`
          : t('text.no_assigned_seat')}
      </Text>
      {showSeatFloor && (
        <Badge roundCorners type="gray">
          <Text elementType="span" size="S" weight="semibold">
            {t('general:deck')}
            <Text elementType="span" size="S" weight="bold">
              {' '}
              {seatFloor}
            </Text>
          </Text>
        </Badge>
      )}
    </Spacing>

    <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
      <Spacing size="XS" alignItems="center">
        <span
          style={{
            width: 8,
            height: 8,
            minWidth: 8,
            borderRadius: '50%',
            backgroundColor: seatZone.color || '#9B9B9B',
          }}
        />
        <Text size="S" color="grayLight" weight="regular">
          {seatZone.label || seatZone.zoneKey}
        </Text>
      </Spacing>
      <LocalizedCurrency
        price={seatPrice}
        currencyStyle="currency"
        size="S"
        color="grayLight"
        weight="regular"
      />
    </Spacing>
  </Spacing>
);

SeatBasedDetails.propTypes = {
  ticketData: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  seatZone: PropTypes.shape({
    color: PropTypes.string,
    zoneKey: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  seatPrice: PropTypes.number.isRequired,
};

/**
 * PassengerDetailsRow component.
 * Renders category, seat, deck, adjacent logo, overbooking and flat-fare copy for standard pricing.
 *
 * @param {object} props - ticketData, getCategoryTranslation, t, showSeat, showSeatFloor, seatFloor, isAdjacentSeat, overbooking, roundTrip, hidePassengerCategoryCopies, flatFareEnabled, flatFareContext, discountsTypes, discountType.
 * @param {object} props.ticketData - Ticket row data for display.
 * @param {Function} props.getCategoryTranslation - Maps adjacent flag and category to translated label.
 * @param {Function} props.t - i18n translation function.
 * @param {boolean} props.showSeat - Whether to show seat-related UI in the row.
 * @param {boolean} props.showSeatFloor - Whether to show deck badge.
 * @param {string|number} props.seatFloor - Deck label when shown.
 * @param {boolean} props.isAdjacentSeat - Whether adjacent seat logo is shown.
 * @param {boolean} props.overbooking - Overbooking state for badges.
 * @param {boolean} props.roundTrip - Round-trip flag for overbooking placement.
 * @param {boolean} props.hidePassengerCategoryCopies - Hides category and seat text when true.
 * @param {boolean} props.flatFareEnabled - Whether flat-fare reward copy may show.
 * @param {string} props.flatFareContext - i18n context for reward ticket label.
 * @param {object} props.discountsTypes - Map of discount type keys.
 * @param {string} props.discountType - Current ticket discount type key.
 * @returns {JSX.Element} PassengerDetailsRow component.
 */
const PassengerDetailsRow = ({
  ticketData,
  getCategoryTranslation,
  t,
  showSeat,
  showSeatFloor,
  seatFloor,
  isAdjacentSeat,
  overbooking,
  roundTrip,
  hidePassengerCategoryCopies,
  flatFareEnabled,
  flatFareContext,
  discountsTypes,
  discountType,
}) => (
  <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
    <Spacing alignItems="center">
      {!hidePassengerCategoryCopies && (
        <>
          <Text size="S" color="grayLight" weight="regular">
            {getCategoryTranslation(ticketData.isAdjacentSeat, ticketData.category)}
          </Text>

          <Text size="S" color="grayLight" weight="regular">
            {ticketData.showSeat && ticketData.seatHasNumber
              ? `${t('general:seat')} ${ticketData.seat}`
              : ''}
          </Text>
        </>
      )}
    </Spacing>

    <Spacing size="XS" alignItems="center">
      {(showSeat || showSeatFloor) && (
        <Spacing size="XS" alignItems="center">
          {showSeat && overbooking && roundTrip && <OverbookingBadge />}
          {showSeatFloor && (
            <Badge roundCorners type="gray">
              <Text elementType="span" size="S" weight="semibold">
                {t('general:deck')}
                <Text elementType="span" size="S" weight="bold">
                  {' '}
                  {seatFloor}
                </Text>
              </Text>
            </Badge>
          )}
        </Spacing>
      )}

      {isAdjacentSeat && <img src={adjacentLogo} alt="Placeholder" className="w-16 h-3" />}

      <Gap columnGap="S">
        {overbooking && !roundTrip ? <OverbookingBadge /> : null}

        {flatFareEnabled && discountsTypes[discountType] && (
          <Text elementType="span" size="S" color="loyaltyLight" weight="semibold" italic>
            {t('label.reward_ticket', { context: flatFareContext })}
          </Text>
        )}
      </Gap>
    </Spacing>
  </Spacing>
);

PassengerDetailsRow.propTypes = {
  ticketData: PropTypes.object.isRequired,
  getCategoryTranslation: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showSeat: PropTypes.bool,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAdjacentSeat: PropTypes.bool,
  overbooking: PropTypes.bool,
  roundTrip: PropTypes.bool,
  hidePassengerCategoryCopies: PropTypes.bool,
  flatFareEnabled: PropTypes.bool,
  flatFareContext: PropTypes.string,
  discountsTypes: PropTypes.object,
  discountType: PropTypes.string,
};

/**
 * PassengerDetails component.
 * Chooses between seat-based and standard passenger row layouts.
 *
 * @param {object} props - ticketData, getCategoryTranslation, t, showSeat, showSeatFloor, seatFloor, isAdjacentSeat, overbooking, roundTrip, hidePassengerCategoryCopies, flatFareEnabled, flatFareContext, discountsTypes, discountType, zoneMap, seatPrice, selectedSeatsForTrip.
 * @param {Object} props.ticketData - Ticket data object
 * @param {Function} props.getCategoryTranslation - Function to get category translation
 * @param {Function} props.t - Translation function
 * @param {boolean} props.showSeat - Whether to show seat
 * @param {boolean} props.showSeatFloor - Whether to show seat floor
 * @param {string|number} props.seatFloor - Seat floor
 * @param {boolean} props.isAdjacentSeat - Whether seat is adjacent
 * @param {boolean} props.overbooking - Whether has overbooking
 * @param {boolean} props.roundTrip - Whether is round trip
 * @param {boolean} props.hidePassengerCategoryCopies - Whether to hide passenger category copies
 * @param {boolean} props.flatFareEnabled - Whether flat fare is enabled
 * @param {string} props.flatFareContext - Flat fare context
 * @param {Object} props.discountsTypes - Discount types mapping
 * @param {string} props.discountType - Discount type
 * @param {Object} props.zoneMap - Zone map keyed by seat fare reference.
 * @param {number} props.seatPrice - Seat price for current ticket row.
 * @param {Array<Object>} props.selectedSeatsForTrip - Selected seats for the trip leg (zone resolution).
 * @returns {JSX.Element} - PassengerDetails component
 */
const PassengerDetails = ({
  ticketData,
  getCategoryTranslation,
  t,
  showSeat,
  showSeatFloor,
  seatFloor,
  isAdjacentSeat,
  overbooking,
  roundTrip,
  hidePassengerCategoryCopies,
  flatFareEnabled,
  flatFareContext,
  discountsTypes,
  discountType,
  zoneMap,
  seatPrice,
  selectedSeatsForTrip,
}) => {
  const useSeatBasedPricing = useShowPricesByGroup();
  const shouldUseSeatBasedPricing =
    useSeatBasedPricing &&
    (ticketData?.category === 'seat' || Object.keys(zoneMap || {}).length > 0);
  const seatZone = resolveSeatZoneForDetails(
    {
      ...ticketData,
      selectedSeatsForTrip,
    },
    zoneMap,
    seatPrice,
  );

  if (shouldUseSeatBasedPricing) {
    return (
      <SeatBasedDetails
        ticketData={ticketData}
        t={t}
        showSeatFloor={showSeatFloor}
        seatFloor={seatFloor}
        seatZone={seatZone}
        seatPrice={seatPrice}
      />
    );
  }

  return (
    <PassengerDetailsRow
      ticketData={ticketData}
      getCategoryTranslation={getCategoryTranslation}
      t={t}
      showSeat={showSeat}
      showSeatFloor={showSeatFloor}
      seatFloor={seatFloor}
      isAdjacentSeat={isAdjacentSeat}
      overbooking={overbooking}
      roundTrip={roundTrip}
      hidePassengerCategoryCopies={hidePassengerCategoryCopies}
      flatFareEnabled={flatFareEnabled}
      flatFareContext={flatFareContext}
      discountsTypes={discountsTypes}
      discountType={discountType}
    />
  );
};

PassengerDetails.propTypes = {
  ticketData: PropTypes.object.isRequired,
  getCategoryTranslation: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showSeat: PropTypes.bool,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAdjacentSeat: PropTypes.bool,
  overbooking: PropTypes.bool,
  roundTrip: PropTypes.bool,
  hidePassengerCategoryCopies: PropTypes.bool,
  flatFareEnabled: PropTypes.bool,
  flatFareContext: PropTypes.string,
  discountsTypes: PropTypes.object,
  discountType: PropTypes.string,
  zoneMap: PropTypes.object,
  seatPrice: PropTypes.number,
  selectedSeatsForTrip: PropTypes.arrayOf(PropTypes.object),
};

/**
 * Gets the price that should be shown for a selected seat before checkout.
 * @param {Object} props - Helper props.
 * @param {number} props.seatIndex - Selected seat position.
 * @param {Object} props.seat - Selected seat object (used for BFO effective quotes).
 * @param {number|string} props.seatPrice - Price stored in the seat layout.
 * @param {number|string} props.fallbackTotal - Reservation total fallback.
 * @param {number|string} props.fallbackPassengerTotal - Per-passenger fallback total.
 * @param {Object} props.fallbackPricing - Full pricing fallback used for discount availability.
 * @param {number} props.selectedCount - Number of selected seats for this trip.
 * @param {number} props.seatsCount - Backwards-compatible alias for selectedCount.
 * @param {boolean} props.useSeatBasedPricing - Whether seat prices should drive display.
 * @param {boolean} props.useBaseFareOverride - Whether supported canonical BFO may apply.
 * @param {boolean} props.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {number|string} Price to display.
 */
export const getPreCheckoutSeatDisplayPrice = ({
  seatIndex = 0,
  seat,
  seatPrice,
  fallbackTotal,
  fallbackPassengerTotal,
  fallbackPricing,
  selectedCount,
  seatsCount,
  useSeatBasedPricing,
  useBaseFareOverride = false,
  mixDiscountFaresByAvailability = false,
}) => {
  if (seat && useBaseFareOverride && isBfoPricing(fallbackPricing)) {
    return resolveBfoEffectiveSeatPrice({ pricing: fallbackPricing, seat }) ?? seatPrice;
  }

  if (useSeatBasedPricing) {
    return seatPrice;
  }

  return getPassengerTotalBySelectionIndex({
    index: seatIndex,
    selectedCount: selectedCount ?? seatsCount,
    defaultPricing: fallbackPricing || { total: fallbackPassengerTotal ?? fallbackTotal },
    mixDiscountFaresByAvailability,
  });
};

/**
 * Gets the pricing object that matches the current trip leg context.
 * @param {Object} props - Helper props.
 * @param {Object} props.fragment - Purchase fragment.
 * @param {string} props.way - Purchase leg.
 * @param {boolean} props.roundTrip - Whether this is a round trip.
 * @param {boolean} props.earnsRoundTripFare - Whether the return leg earns the round-trip fare.
 * @returns {Object|undefined} Pricing for the fragment.
 */
const getPricingForFragment = ({ fragment, way, roundTrip, earnsRoundTripFare }) => {
  if (!roundTrip) return fragment?.pricing;
  if (way !== 'returns') return fragment?.departureRoundTripPricing;

  return earnsRoundTripFare ? fragment?.roundTripPricing : fragment?.pricing;
};

/**
 * Builds the per-passenger pricing object used before checkout.
 * @param {Object} props - Helper props.
 * @param {Object} props.reservation - Purchase reservation.
 * @param {string} props.way - Purchase leg.
 * @param {boolean} props.roundTrip - Whether this is a round trip.
 * @param {Object} [props.departureReservation] - Departure leg, to check the pair.
 * @param {boolean} [props.validateRoundTripDiscount] - Whether the brand validates the pair.
 * @returns {Object} Per-passenger display pricing.
 */
export const getPreCheckoutPassengerDisplayPricing = ({
  reservation,
  way,
  roundTrip,
  departureReservation,
  validateRoundTripDiscount,
}) => {
  const fragments = reservation?.fragments || [];
  // Decided once for the whole leg: a round trip with connections must not mix the single and
  // round-trip fares across its own fragments.
  const earnsRoundTripFare =
    !validateRoundTripDiscount ||
    roundTripFareApplies({
      departureLeg: legFromFragments(departureReservation?.fragments),
      returnLeg: legFromFragments(fragments),
    });
  const fragmentPricings = fragments
    .map((fragment) => getPricingForFragment({ fragment, way, roundTrip, earnsRoundTripFare }))
    .filter(Boolean);

  if (!fragmentPricings.length) return reservation?.pricing || {};

  const total = fragmentPricings.reduce((sum, pricing) => sum + Number(pricing?.total || 0), 0);
  const totalBeforeDiscount = fragmentPricings.reduce(
    (sum, pricing) => sum + Number(pricing?.totalBeforeDiscount || 0),
    0,
  );
  const discountAvailabilities = fragmentPricings
    .map(getDiscountAvailability)
    .filter((availability) => availability != null);
  const providerDiscountsByName = fragmentPricings.reduce((discountsByName, pricing) => {
    (pricing?.providerDiscounts || []).forEach((discount) => {
      const current = discountsByName[discount.name] || {
        ...discount,
        total: 0,
        availability: null,
      };
      const discountAvailability = Number(discount?.availability);
      discountsByName[discount.name] = {
        ...current,
        ...discount,
        total: Number(current.total || 0) + Number(discount.total || 0),
        availability: Number.isFinite(discountAvailability)
          ? Math.min(current.availability ?? discountAvailability, discountAvailability)
          : current.availability,
      };
    });
    return discountsByName;
  }, {});

  return {
    ...reservation?.pricing,
    ...fragmentPricings[0],
    total: total || reservation?.pricing?.total,
    totalBeforeDiscount: totalBeforeDiscount || reservation?.pricing?.totalBeforeDiscount,
    discountAvailability: discountAvailabilities.length
      ? Math.min(...discountAvailabilities)
      : reservation?.pricing?.discountAvailability,
    providerDiscounts: Object.keys(providerDiscountsByName).length
      ? Object.values(providerDiscountsByName)
      : reservation?.pricing?.providerDiscounts,
  };
};

/**
 * Gets the per-passenger total used before checkout.
 * @param {Object} reservation - Purchase reservation.
 * @returns {number|string|undefined} Per-passenger total.
 */
export const getPreCheckoutPassengerDisplayTotal = (reservation) => {
  return getPreCheckoutPassengerDisplayPricing({ reservation })?.total;
};

/**
 * Displays a pre-checkout selected seat item.
 * @param {Object} props - Component props.
 * @param {Object} props.seat - Seat information.
 * @param {number} props.index - Seat index.
 * @param {number|string} props.price - Price to display.
 * @param {Function} props.t - Translation function.
 * @param {boolean} props.showSeatFareDescription - Whether to show seat fare description.
 * @returns {JSX.Element|null} Pre-checkout seat row.
 */
const PreCheckoutSeat = ({ seat, index, price, t, showSeatFareDescription }) => {
  const displayPrice = price ?? seat.price;

  if (displayPrice == null) return null;

  return (
    <>
      <div className="passenger-row">
        <div className="flex gap-2">
          <p className="text-sm font-semibold">{`${t('passengers:label.passenger')} ${
            index + 1
          }`}</p>
          <p className="text-sm font-bold">{seat.category === 'VIP' && seat.category}</p>
        </div>
      </div>
      <RowComponent
        label={t('label.price')}
        price={displayPrice}
        description={getFareDescription(seat.seatFareDescription, showSeatFareDescription)}
      />
    </>
  );
};

PreCheckoutSeat.propTypes = {
  seat: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  t: PropTypes.func.isRequired,
  showSeatFareDescription: PropTypes.bool,
};

const propTypes = {
  reservation: PropTypes.shape({
    tripSlug: PropTypes.string.isRequired,
    tickets: PropTypes.array.isRequired,
    pricing: PropTypes.shape({
      total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }).isRequired,
    selectedSeats: PropTypes.array,
  }).isRequired,
  way: PropTypes.oneOf(['departs', 'returns']).isRequired,
  segment: PropTypes.string,
  titleLabel: PropTypes.string,
  currency: PropTypes.string,
  externalCouponCode: PropTypes.string,
  selectedSeatsBeforeCheckout: PropTypes.array,
  displayPricingBeforeCheckout: PropTypes.bool,
  selectedSeatsTotal: PropTypes.number,
  roundTrip: PropTypes.bool,
  isOpen: PropTypes.bool,
  hasCollapsibleTrigger: PropTypes.bool,
};

/**
 * PurchasePricingPassengerTrip component.
 * Displays the pricing information for each passenger or seat based on the configuration.
 *
 * @param {Object} props - Component props
 * @param {string} props.way - Trip leg (`departs` or `returns`).
 * @param {Object} props.reservation - Reservation object containing trip and tickets information
 * @param {string} props.segment - Trip segment.
 * @param {string} props.titleLabel - Title label for the section (e.g., "Detalle de ida")
 * @param {string} props.currency - Currency code
 * @param {string} props.externalCouponCode - External coupon code if available
 * @param {Array} props.selectedSeatsBeforeCheckout - Selected seats before checkout
 * @param {boolean} props.displayPricingBeforeCheckout - Whether to display pricing before checkout
 * @param {number} props.selectedSeatsTotal - Total amount for selected seats
 * @param {boolean} props.roundTrip - Whether the trip is a round trip
 * @param {boolean} props.isOpen - Whether the collapsible section is open
 * @param {boolean} props.hasCollapsibleTrigger - Whether to show collapsed passenger view
 * @returns {JSX.Element} - PurchasePricingPassengerTrip component
 */
const PurchasePricingPassengerTrip = ({
  reservation,
  way,
  segment,
  titleLabel,
  currency,
  externalCouponCode,
  selectedSeatsBeforeCheckout,
  displayPricingBeforeCheckout,
  selectedSeatsTotal,
  roundTrip,
  isOpen,
  hasCollapsibleTrigger,
}) => {
  const features = useWhitelabelFeatures();
  const {
    PRECHECKOUT_SEATS,
    PURCHASE_PRICING_DETAILED_DISCOUNT,
    FLAT_FARE_ENABLED,
    FLAT_FARE_CONTEXT,
    HIDE_PASSENGER_CATEGORY_COPIES,
    SHOW_SEAT_FARE_DESCRIPTION,
    SHOW_ORIGINAL_PRICE_ON_CHECKOUT,
    SHOW_PASSENGER_PRICING_BREAKDOWN,
    CUSTOM_MANAGEMENT_COSTS_LABEL,
    VALIDATE_ROUND_TRIP_DISCOUNT,
    USE_CARRIER_FARE_BASIS,
    USE_SEAT_BASED_PRICING,
  } = features;
  const showPricesByGroup = useShowPricesByGroup();
  const useSeatPriceTotals = Boolean(USE_SEAT_BASED_PRICING);
  const mixDiscountFaresByAvailability = useMixDiscountFaresByAvailability();
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();

  const env = useWhitelabelEnvs();
  const breakdownValuesThatCanBeDisplayedAtCero =
    env.theme?.pricing?.breakdownValuesThatCanBeDisplayedAtCero || [];
  const purchase = usePurchase();
  const useBaseFareOverride = isBfoSupportedJourney({
    purchase,
    features,
    brand: env.brand,
  });
  const { t } = useTranslation(['purchase', 'trips', 'passengers', 'general']);
  const seatsBus = useSeatsBus();
  const { tickets, pricing, tripSlug, selectedSeats } = reservation;
  const discountsTypes = {
    single_trip_flat_fare_discount: 'single_trip_flat_fare_discount',
    departure_on_round_trip_flat_fare_discount: 'departure_on_round_trip_flat_fare_discount',
    single_on_round_trip_flat_fare_discount: 'single_on_round_trip_flat_fare_discount',
  };

  const overbooking = hasOversoldSeats(selectedSeats);
  const { zoneMap } = usePurchasePricingSeatZones({
    purchase,
    way,
    reservation,
    seatsBus,
    tripSlug,
    selectedSeatsBeforeCheckout,
  });

  const { total } = pricing;
  const passengerDisplayPricing = getPreCheckoutPassengerDisplayPricing({
    reservation,
    way,
    roundTrip,
    departureReservation: purchase?.departs,
    validateRoundTripDiscount: VALIDATE_ROUND_TRIP_DISCOUNT,
  });
  const passengerDisplayTotal = passengerDisplayPricing?.total;
  const pricingTotal = tickets.length === 0 ? passengerDisplayTotal : total;

  // Determine if we should use selected seats for pricing based on feature flags
  const useSelectedSeats = showPricesByGroup && !tickets.length;

  /**
   * Get category translation
   * @param {boolean} isAdjacentSeat - Whether it's an adjacent seat
   * @param {string} category - Category name
   * @returns {string} - Translated category text
   */
  const getCategoryTranslation = (isAdjacentSeat, category) =>
    t(`general:category_${isAdjacentSeat ? 'adjacent_seat' : category}`, {
      context: env.brand,
    });

  // Final total price, computed via the shared usePricingTotal hook (also used by PurchasePricing).
  // Seat prices are authoritative for seat-based pricing tenants even when the
  // grouped-price visual experiment is disabled for the current route.
  const finalTotal = usePricingTotal({
    useSelectedSeats,
    selectedSeatsTotal,
    displayPricingBeforeCheckout,
    selectedSeatsBeforeCheckout,
    useSeatBasedPricing: useSeatPriceTotals,
    defaultPricing: passengerDisplayPricing,
    mixDiscountFaresByAvailability,
    useBaseFareOverride,
    total: pricingTotal,
  });

  // Determine which tickets to display based on selected seats configuration
  const ticketsTarget = useSelectedSeats ? selectedSeatsBeforeCheckout : tickets;

  // On a carrier-basis leg the section row shows the sum of window fares, so the passenger rows
  // below read as what each one actually costs after its discount. Only when EVERY ticket on the
  // leg carries a window fare: a leg mixing seats locked before and after the snapshot shipped
  // would otherwise sum a subset and present it as the whole, which is worse than showing the
  // charge. Falls back to `finalTotal`, the amount charged, which is what every other brand shows.
  const carrierFares =
    USE_CARRIER_FARE_BASIS && !useSelectedSeats
      ? ticketsTarget.map((item) => carrierFareBasis(item))
      : [];
  const carrierLegFare =
    carrierFares.length > 0 && carrierFares.every(Boolean)
      ? carrierFares.reduce((sum, carrier) => sum + carrier.fare, 0)
      : null;
  // ONE decision for the whole leg, never one per ticket. Deciding per ticket let a seat that
  // has a window fare render on the carrier basis underneath a section row that had already
  // fallen back to the charge — the two fare bases side by side on the same leg, which is the
  // defect this change exists to remove. Also keeps the seat-based path out: it builds no
  // carrier fares, so this is empty there.
  //
  // Each ticket is then handed the entry the section row was summed from, rather than the
  // decision plus a second `carrierFareBasis` call of its own. The leg and the ticket cannot
  // disagree about a number neither of them recomputes.
  const legCarrierFares = carrierLegFare !== null ? carrierFares : [];

  return (
    <>
      {titleLabel && (
        <RowComponent
          label={titleLabel}
          price={carrierLegFare ?? finalTotal}
          isMain
          titleSize="M"
        />
      )}
      {ticketsTarget.map((item, index) => {
        const ticketData = processTicketData(
          item,
          useSelectedSeats,
          t,
          PURCHASE_PRICING_DETAILED_DISCOUNT,
          legCarrierFares[index] ?? null,
        );
        const seatPrice = (Number(ticketData?.amount) || 0) + (Number(ticketData?.taxes) || 0);
        const matchedPassenger = purchase.passengers?.find(
          (passenger) => passenger.id === ticketData.passengerId,
        );
        const lapChild = matchedPassenger?.meta?.lapChild;
        const hasLapChild = hasLapChildData(lapChild);
        const lapChildName = hasLapChild
          ? `${lapChild.firstName || ''} ${lapChild.lastName || ''}`.trim()
          : '';

        return (
          <div
            className={`flex flex-col transition-all duration-800 ease-in-out ${
              isOpen || hasCollapsibleTrigger ? 'gap-y-3' : 'gap-y-0'
            }`}
            key={`${tripSlug}-${ticketData.passengerId || index}`}
          >
            <PassengerTicketHeader
              passengerName={ticketData.passengerName}
              showSeat={ticketData.showSeat}
              showSeatFloor={ticketData.showSeatFloor}
              seatHasNumber={ticketData.seatHasNumber}
              seat={ticketData.seat}
              category={ticketData.category}
              seatFloor={ticketData.seatFloor}
              isAdjacentSeat={ticketData.isAdjacentSeat}
              discountType={item.discountType}
              hidePassengerCategoryCopies={HIDE_PASSENGER_CATEGORY_COPIES}
              flatFareEnabled={FLAT_FARE_ENABLED}
              flatFareContext={FLAT_FARE_CONTEXT}
              discountsTypes={discountsTypes}
              getCategoryTranslation={getCategoryTranslation}
              t={t}
              roundTrip={roundTrip}
              overbooking={overbooking}
              hasCollapsibleTrigger={hasCollapsibleTrigger}
              // On the platform basis this is `amount`, the fare the discount is computed
              // against — a row showing `total` made the panel read 116000 - 35000 = 116000
              // (MID-642). On the carrier basis it is what this passenger costs, with the window
              // fare it was discounted from summed into the section row above. `?? amount` keeps
              // the seat-based path, which builds no `rowPrice`, on the value it always had.
              totalPrice={ticketData.rowPrice ?? ticketData.amount}
              isOpen={isOpen}
              discountAmount={ticketData.carrierDiscount ?? ticketData.discountAmount}
              hasEarlyDiscount={ticketData.hasEarlyDiscount}
              discountLabels={ticketData.discountLabels}
              brand={env.brand}
              currency={currency}
              showDiscount={PURCHASE_PRICING_DETAILED_DISCOUNT}
              showDiscountUrgency={urgencyDiscountUIEnabled}
            />

            <div
              data-testid="passenger-collapsible"
              className={`flex flex-col gap-y-3 overflow-hidden transition-all duration-1000 ease-in-out ${
                isOpen || hasCollapsibleTrigger ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {/* Passenger category and seat information - inside collapsible section */}
              <PassengerDetails
                ticketData={ticketData}
                getCategoryTranslation={getCategoryTranslation}
                t={t}
                zoneMap={zoneMap}
                seatPrice={seatPrice}
                selectedSeatsForTrip={
                  useSelectedSeats ? selectedSeatsBeforeCheckout : selectedSeats
                }
                showSeat={ticketData.showSeat}
                showSeatFloor={ticketData.showSeatFloor}
                seatHasNumber={ticketData.seatHasNumber}
                seat={ticketData.seat}
                category={ticketData.category}
                seatFloor={ticketData.seatFloor}
                isAdjacentSeat={ticketData.isAdjacentSeat}
                overbooking={overbooking}
                roundTrip={roundTrip}
                hidePassengerCategoryCopies={HIDE_PASSENGER_CATEGORY_COPIES}
                flatFareEnabled={FLAT_FARE_ENABLED}
                flatFareContext={FLAT_FARE_CONTEXT}
                discountsTypes={discountsTypes}
                discountType={item.discountType}
              />

              {hasLapChild && (
                <div className="flex flex-col gap-y-1">
                  <Text size="S" weight="semibold" color="grayStrong">
                    {lapChildName}
                  </Text>
                  <Spacing alignItems="center">
                    <Text size="S" color="grayLight" weight="regular">
                      {getCategoryTranslation(false, 'child_in_arms')}
                    </Text>
                  </Spacing>
                </div>
              )}

              <TicketPriceDetails
                tripSlug={tripSlug}
                passengerId={ticketData.passengerId}
                segment={segment}
                amount={ticketData.amount}
                carrierFare={ticketData.carrierFare}
                discountAmount={ticketData.carrierDiscount ?? ticketData.discountAmount}
                hasEarlyDiscount={ticketData.hasEarlyDiscount}
                discountLabels={ticketData.discountLabels}
                subtotal={ticketData.subtotal}
                taxes={ticketData.taxes}
                total={ticketData.total}
                toll={ticketData.toll}
                boardingTax={ticketData.boardingTax}
                managementCosts={ticketData.managementCosts}
                showBreakdown={SHOW_PASSENGER_PRICING_BREAKDOWN && !useSelectedSeats}
                canShowAtZero={breakdownValuesThatCanBeDisplayedAtCero}
                customManagementCostsLabel={CUSTOM_MANAGEMENT_COSTS_LABEL}
                seatFareDescription={ticketData.seatFareDescription}
                brand={env.brand}
                externalCouponCode={externalCouponCode}
                showSeatFareDescription={SHOW_SEAT_FARE_DESCRIPTION}
                showOriginalPriceOnCheckout={SHOW_ORIGINAL_PRICE_ON_CHECKOUT}
                seatPrice={seatPrice}
                showDiscountUrgency={urgencyDiscountUIEnabled}
                t={t}
              />
            </div>
          </div>
        );
      })}

      {PRECHECKOUT_SEATS &&
        tickets.length === 0 &&
        selectedSeatsBeforeCheckout.map((seat, index) => (
          <PreCheckoutSeat
            key={`seat-${seat.number || seat.id || `position-${index}`}`}
            seat={seat}
            index={index}
            price={getPreCheckoutSeatDisplayPrice({
              seatIndex: index,
              seat,
              seatPrice: seat.price,
              fallbackTotal: total,
              fallbackPassengerTotal: passengerDisplayTotal,
              fallbackPricing: passengerDisplayPricing,
              selectedCount: selectedSeatsBeforeCheckout.length,
              useSeatBasedPricing: useSeatPriceTotals,
              useBaseFareOverride,
              mixDiscountFaresByAvailability,
            })}
            t={t}
            showSeatFareDescription={showPricesByGroup && SHOW_SEAT_FARE_DESCRIPTION}
          />
        ))}
    </>
  );
};

PurchasePricingPassengerTrip.propTypes = propTypes;

export default PurchasePricingPassengerTrip;
